import { useCallback, useMemo, useRef } from 'react';
import { FilterSelection, isSelectionActive } from '../views/FileManager/components/FilterPanel';

export interface LocationFilterStore {
  getFilter: (key: string) => FilterSelection | undefined;
  setFilter: (key: string, selection: FilterSelection) => void;
  clearFilter: (key: string) => void;
}

// Keyed per section + folder: all three sections share the literal folder id
// "root", so pathname alone disambiguates them.
export const buildFilterStoreKey = (pathname: string, folderId: string) => `${pathname}:${folderId}`;

// In-memory per-location filter store. Lives (as a ref) inside the FileManager
// tree, so all entries are discarded when the user leaves the module. Never
// drives rendering — UI reads the appliedSelection state synced from it.
export const useLocationFilterStore = (): LocationFilterStore => {
  const storeRef = useRef<Map<string, FilterSelection>>(new Map());

  const getFilter = useCallback((key: string) => storeRef.current.get(key), []);

  const setFilter = useCallback((key: string, selection: FilterSelection) => {
    // Only non-empty selections are kept, so the map stays compact.
    if (isSelectionActive(selection)) storeRef.current.set(key, selection);
    else storeRef.current.delete(key);
  }, []);

  const clearFilter = useCallback((key: string) => {
    storeRef.current.delete(key);
  }, []);

  // Stable identity so consumers can depend on the store itself.
  return useMemo(() => ({ getFilter, setFilter, clearFilter }), [getFilter, setFilter, clearFilter]);
};
